import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import {
  FailedParsedSource,
  KnowledgeSocketEventsSchemas,
} from 'apps/aiAutomation/store/knowledge/schemas'
import { handleError } from 'shared/api/lib/errors/handlers'
import { AiBusinessKnowledgeApi } from 'shared/api/requests/aiBusinessKnowledge'
import {
  KnowledgeEntryState,
  KnowledgeEntryType,
  KnowledgeErrorCode,
  LinkEntry,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'
import { accountNotificationsListener } from 'utils/services/notificationsService'

interface UseProcessLinkToTextProps {
  entry: LinkEntry | null
}

const LAST_STEP_TIMEOUT = 2000

export const useProcessLinkToText = ({ entry }: UseProcessLinkToTextProps) => {
  const isNewEntry = !entry

  const [error, setError] = useState<KnowledgeErrorCode | null>(null)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [isLastStepOfLoading, setIsLastStepOfLoading] = useState<boolean>(false)
  const [processedEntry, setProcessedEntry] = useState<LinkEntry | null>(null)

  const entryIdRef = useRef<string | null>(null)

  const processLink = async (url?: string) => {
    const id = isNewEntry ? uuid() : entry.id

    entryIdRef.current = id

    AiAutomationAnalytics.logKnowledgeEntryScanned({
      id,
      entry_state: isNewEntry ? 'new' : 'existing',
    })

    try {
      setError(null)
      setIsProcessing(true)
      await AiBusinessKnowledgeApi.preview({
        body: {
          id,
          type: KnowledgeEntryType.LINK,
          data: {
            url: url || entry?.data.url || '',
          },
        },
      })
    } catch (cachedError) {
      handleError(cachedError)
      setError(KnowledgeErrorCode.INTERNAL_ERROR)
      setIsProcessing(false)
    }
  }

  const handleLinkParsedEvent = ({ data }: { data: LinkEntry | FailedParsedSource }) => {
    setIsProcessing(false)

    if (data.id !== entryIdRef.current) {
      return
    }

    if (data.state === KnowledgeEntryState.FAILED) {
      setError(data.error_code || KnowledgeErrorCode.INTERNAL_ERROR)

      return
    }

    setIsLastStepOfLoading(true)
    setTimeout(() => {
      setProcessedEntry(data)
      setIsLastStepOfLoading(false)
    }, LAST_STEP_TIMEOUT)
  }

  useEffect(() => {
    accountNotificationsListener.on(KnowledgeSocketEventsSchemas.linkParsed, handleLinkParsedEvent)

    return () => {
      accountNotificationsListener.removeListener(
        KnowledgeSocketEventsSchemas.linkParsed,
        handleLinkParsedEvent,
      )
    }
  }, [])

  return {
    isProcessing,
    isLastStepOfLoading,
    error,
    processedEntry,
    processLink,
  }
}
